import * as React from 'react';
import { RadioGroup } from '@headlessui/react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/**
 * On the server, React emits a warning when calling `useLayoutEffect`.
 * This is because neither `useLayoutEffect` nor `useEffect` run on the server.
 * We use this safe version which suppresses the warning by replacing it with a noop on the server.
 *
 * See: https://reactjs.org/docs/hooks-reference.html#uselayouteffect
 */
var useLayoutEffect = Boolean(globalThis === null || globalThis === void 0 ? void 0 : globalThis.document)
    ? React.useLayoutEffect
    : function () { };

// We `toString()` to prevent bundlers from trying to `import { useId } from 'react';`
var useReactId = React['useId'.toString()] || (function () { return undefined; });
var count = 0;
var useId = function useId(deterministicId) {
    var _a = React.useState(useReactId()), id = _a[0], setId = _a[1];
    // React versions older than 18 will have client-side ids only.
    useLayoutEffect(function () {
        if (!deterministicId)
            setId(function (reactId) { return reactId !== null && reactId !== void 0 ? reactId : String(count++); });
    }, [deterministicId]);
    return deterministicId || (id ? "wartek-".concat(id) : '');
};

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".SelectionBox-module_srOnly__3vWe8{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border-width:0}";
var styles = {"srOnly":"SelectionBox-module_srOnly__3vWe8"};
styleInject(css_248z);

var SelectionBox = function (props) {
    var helperId = useId();
    var onChange = props.onChange, isDisabled = props.disabled, value = props.value, children = props.children, selectionBoxProps = __rest(props, ["onChange", "disabled", "value", "children"]);
    var handleChange = function (value) {
        onChange === null || onChange === void 0 ? void 0 : onChange(value);
    };
    return (React.createElement(RadioGroup, __assign({ disabled: isDisabled, value: value, onChange: handleChange, id: "custom-select-".concat(helperId), className: "mt-2" }, selectionBoxProps),
        React.createElement(RadioGroup.Label, { className: styles.srOnly, role: "" }, props['aria-label']),
        children));
};

var DESCRIPTION_NAME = 'SelectionBoxOption';
var SelectionBoxOption = React.forwardRef(function (props, ref) {
    var className = props.className, value = props.value, children = props.children, disabled = props.disabled, selectionBoxOptionProps = __rest(props, ["className", "value", "children", "disabled"]);
    return (React.createElement(RadioGroup.Option, __assign({ ref: ref, value: value, className: className, disabled: value.disabled || disabled || false }, selectionBoxOptionProps), function (_a) {
        var checked = _a.checked, active = _a.active;
        return typeof children === 'function'
            ? children({ checked: checked, active: active })
            : children;
    }));
});
SelectionBoxOption.displayName = DESCRIPTION_NAME;

export { SelectionBox, SelectionBoxOption };
